import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import './BundleDefinitionsPage.css';
import Pagination from '../components/Pagination';

const initialFormState = {
  bundleSku: '',
  bundleName: '',
  components: [{ sku: '', quantity: '' }],
};

function BundleDefinitionsPage() {
  const { currentUser, canAccessPage, isAdmin } = useAuth();
  const [bundles, setBundles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;
  
  const [formState, setFormState] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canWrite = isAdmin || canAccessPage('inventory');

  const fetchBundles = useCallback(async (page = currentPage) => {
    setIsLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/v1/bundles?page=${page}&limit=${itemsPerPage}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch bundles.');
      const data = await response.json();
      setBundles(data.bundles || []);
      setTotalItems(data.totalCount || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchBundles(currentPage);
  }, [fetchBundles, currentPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleComponentChange = (index, field, value) => {
    const newComponents = [...formState.components];
    newComponents[index][field] = value;
    setFormState(prev => ({ ...prev, components: newComponents }));
  };

  const addComponent = () => {
    setFormState(prev => ({
      ...prev,
      components: [...prev.components, { sku: '', quantity: '' }],
    }));
  };

  const removeComponent = (index) => {
    const newComponents = formState.components.filter((_, i) => i !== index);
    setFormState(prev => ({ ...prev, components: newComponents }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canWrite) return;
    setIsSubmitting(true);
    setError('');

    const url = editingId
      ? `/api/v1/bundles/${editingId}`
      : '/api/v1/bundles';
    
    const method = editingId ? 'PUT' : 'POST';

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formState),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || `Failed to ${editingId ? 'update' : 'create'} bundle.`);
      }

      alert(`Bundle ${editingId ? 'updated' : 'created'} successfully!`);
      handleCancelEdit();
      fetchBundles();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (bundle) => {
    setEditingId(bundle.id);
    setFormState({
      bundleSku: bundle.bundleSku || '',
      bundleName: bundle.bundleName || '',
      components: bundle.components || [{ sku: '', quantity: '' }],
    });
  };

  const handleDelete = async (id) => {
    if (!canWrite) return;
    if (window.confirm('Are you sure you want to delete this bundle definition?')) {
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(`/api/v1/bundles/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to delete bundle.');
        alert('Bundle deleted successfully!');
        fetchBundles();
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormState(initialFormState);
  };

  const handleExportCSV = () => {
    if (bundles.length === 0) {
      alert('No bundles to export.');
      return;
    }

    const headers = ["Bundle SKU", "Bundle Name", "Component SKU", "Component Quantity"];
    const rows = [];

    bundles.forEach(bundle => {
      if (bundle.components.length > 0) {
        bundle.components.forEach((comp, index) => {
          rows.push([
            index === 0 ? bundle.bundleSku : '',
            index === 0 ? bundle.bundleName : '',
            comp.sku,
            comp.quantity
          ].map(value => `"${String(value).replace(/"/g, '""')}"`).join(','));
        });
      } else {
        rows.push([bundle.bundleSku, bundle.bundleName, '', ''].map(value => `"${String(value).replace(/"/g, '""')}"`).join(','));
      }
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'bundle-definitions.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.trim().split('\n').slice(1); // Skip header row
        const bundleMap = {};

        lines.forEach(line => {
          const [bundleSku, bundleName, compSku, compQty] = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          if (bundleSku) {
            if (!bundleMap[bundleSku]) {
              bundleMap[bundleSku] = { bundleSku, bundleName, components: [] };
            }
            if (compSku && compQty) {
              bundleMap[bundleSku].components.push({ sku: compSku, quantity: parseFloat(compQty) });
            }
          }
        });

        const importedBundles = Object.values(bundleMap);
        setIsSubmitting(true);
        const token = await currentUser.getIdToken();
        const response = await fetch('/api/v1/bundles/bulk-import', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ bundles: importedBundles }) });
        if (!response.ok) { const errData = await response.json(); throw new Error(errData.message || 'Failed to import bundles.'); }
        const result = await response.json();
        alert(`Import successful! ${result.summary}`);
        fetchBundles();
      } catch (err) { setError(`Import Error: ${err.message}`); } finally { setIsSubmitting(false); event.target.value = null; }
    };
    reader.readAsText(file);
  };

  return (
    <div className="bundle-defs-container">
      <div className="page-header">
        <h1>Bundle Definitions</h1>
        {canWrite && (
          <div className="header-actions">
            <button onClick={handleExportCSV} className="export-btn">Export CSV</button>
            <label htmlFor="csv-import-input" className="import-btn">
              Import CSV
            </label>
            <input id="csv-import-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImportCSV} disabled={isSubmitting} />
          </div>
        )}
      </div>

      {canWrite && (
        <div className="bundle-form-section">
          <h2>{editingId ? 'Edit Bundle' : 'Add New Bundle'}</h2>
          <form onSubmit={handleSubmit} className="bundle-form">
            <div className="form-grid">
              <div className="form-group">
                <label>Bundle SKU*</label>
                <input name="bundleSku" value={formState.bundleSku} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Bundle Name</label>
                <input name="bundleName" value={formState.bundleName} onChange={handleInputChange} />
              </div>
            </div>
            
            <h4>Components</h4>
            <div className="components-list">
              {formState.components.map((comp, index) => (
                <div key={index} className="component-item">
                  <input 
                    name="sku" 
                    placeholder="Component SKU" 
                    value={comp.sku} 
                    onChange={(e) => handleComponentChange(index, 'sku', e.target.value)} 
                    required 
                  />
                  <input 
                    type="number"
                    name="quantity" 
                    placeholder="Quantity" 
                    value={comp.quantity} 
                    onChange={(e) => handleComponentChange(index, 'quantity', e.target.value)} 
                    required 
                    step="0.01"
                  />
                  <button type="button" onClick={() => removeComponent(index)} className="remove-comp-btn">-</button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addComponent} className="add-comp-btn">+ Add Component</button>

            <div className="form-actions">
              {editingId && <button type="button" onClick={handleCancelEdit} className="cancel-btn">Cancel</button>}
              <button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : (editingId ? 'Update Bundle' : 'Add Bundle')}</button>
            </div>
          </form>
        </div>
      )}

      <div className="bundle-table-section">
        <h2>Existing Bundles</h2>
        {isLoading && <p>Loading bundles...</p>}
        {error && <p className="error-message">{error}</p>}
        <div className="table-wrapper">
          <table className="bundle-table">
            <thead>
              <tr>
                <th>Bundle SKU</th>
                <th>Bundle Name</th>
                <th>Components</th>
                {canWrite && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {bundles.map((bundle) => (
                <tr key={bundle.id}>
                  <td>{bundle.bundleSku}</td>
                  <td>{bundle.bundleName}</td>
                  <td>
                    <ul>
                      {bundle.components.map((c, i) => (
                        <li key={i}>{c.quantity}m of {c.sku}</li>
                      ))}
                    </ul>
                  </td>
                  {canWrite && (
                    <td className="actions-cell">
                      <button onClick={() => handleEdit(bundle)} className="edit-btn">Edit</button>
                      <button onClick={() => handleDelete(bundle.id)} className="delete-btn">Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
}

export default BundleDefinitionsPage;
